'use client';

import { useMemo } from 'react';
import styled from 'styled-components';

import Bullet from './Bullet';
import { parseSanitizedHTML } from '@/utils/helpers';
import { log } from '@/utils/logger';

const LoadingSkeleton = styled.div<{ $width: string; $isFirst?: boolean }>`
  height: 18px;
  margin-bottom: 16px;
  margin-top: ${props => (props.$isFirst ? '0' : '16px')};
  width: ${props => props.$width};
`;

type LineProps = {
  $level: number;
  $icon?: string;
};

const Line = styled.div<LineProps>`
  color: ${props => (props.$icon === 'caution' ? 'red' : 'inherit')};
  font-size: 15px;
  line-height: 24px;
  margin-left: ${props => (props.$level ? props.$level * 30 : 0)}px;
  position: relative;
  padding-left: 37px;
  width: auto;
  word-break: break-word;

  &:not(:first-child) {
    margin-top: 1rem;
  }

  p {
    margin-top: 0;
  }
`;

type Meta = {
  level?: number;
  icon?: 'caution' | 'note' | 'reminder';
  color?: string;
};

type LineData = {
  id: string;
  title: string;
  order: number;
  meta?: Meta;
};

type LinesProps = {
  data?: LineData[];
  isLoading?: boolean;
};

// Helper function to get random width between 50% and 100%
const getRandomWidth = () => {
  return `${Math.floor(Math.random() * 51) + 50}%`;
};

export default function Lines({ data, isLoading }: LinesProps) {
  // Memoize parsed lines to avoid re-parsing HTML on every render
  const parsedLines = useMemo(() => {
    if (!data) return null;
    
    return data.map(({ meta = {}, id, title }: LineData) => {
      const { level = 0, icon, color } = meta;
      const parsedContent = parseSanitizedHTML(title, true);

      return (
        <Line key={id} $level={level} $icon={icon}>
          <Bullet icon={icon} color={color} />
          {parsedContent}
        </Line>
      );
    });
  }, [data]);
  
  if(isLoading) {
    return (
      <>
        <LoadingSkeleton className="pulse" $width={getRandomWidth()} $isFirst={true} />
        <LoadingSkeleton className="pulse" $width={getRandomWidth()} />
        <LoadingSkeleton className="pulse" $width={getRandomWidth()} />
        <LoadingSkeleton className="pulse" $width={getRandomWidth()} />
        <LoadingSkeleton className="pulse" $width={getRandomWidth()} />
        <LoadingSkeleton className="pulse" $width={getRandomWidth()} />
      </>
    );
  }

  if(!data) {
    log.warning({ event: 'Lines - no data', message: '[Guide Lines] No data found' });

    return null;
  }

  // .sort((a, b) => a.order - b.order) - ill-sorted order prop for translations from BE
  return parsedLines;
}
